import { Alert, Badge, Button, Card, CardContent, Dialog, Field, Input, PageHeader, Switch, toast } from '@1k5-tech-es/grado-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Pencil, Plus } from 'lucide-react';
import { useState, type FormEvent } from 'react';

import { api, ApiError } from '../../api/client';
import type { Team } from '../../api/types';
import { ErrorBlock, LoadingBlock } from '../../components/ui';

export function TeamsPage() {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<Team | 'new' | null>(null);
  const teams = useQuery({
    queryKey: ['teams'],
    queryFn: () => api.get<{ teams: Team[] }>('/api/teams').then((r) => r.teams),
  });

  return (
    <>
      <PageHeader
        title="Equipos"
        subtitle="El código del equipo es el que aparece en la cabecera del informe de cada asesor."
        actions={
          <Button icon={Plus} onClick={() => setEditing('new')}>
            Nuevo equipo
          </Button>
        }
      />
      {teams.isPending ? (
        <LoadingBlock />
      ) : teams.isError ? (
        <ErrorBlock error={teams.error} />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {teams.data.map((team) => (
            <Card key={team.id}>
              <CardContent className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-lg font-bold text-primary-text">{team.code}</span>
                    {!team.isActive && <Badge>Inactivo</Badge>}
                  </div>
                  <div className="text-sm text-muted-foreground">{team.name}</div>
                  <div className="mt-2 text-xs text-muted-foreground">{team.advisorCount} asesores</div>
                </div>
                <Button variant="ghost" size="sm" icon={Pencil} onClick={() => setEditing(team)}>
                  Editar
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
      {editing && (
        <TeamDialog
          team={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={(code) => {
            setEditing(null);
            toast.success(editing === 'new' ? `Equipo ${code} creado` : `Equipo ${code} guardado`);
            void queryClient.invalidateQueries({ queryKey: ['teams'] });
            void queryClient.invalidateQueries({ queryKey: ['advisors'] });
          }}
        />
      )}
    </>
  );
}

function TeamDialog({ team, onClose, onSaved }: { team: Team | null; onClose: () => void; onSaved: (code: string) => void }) {
  const [code, setCode] = useState(team?.code ?? '');
  const [name, setName] = useState(team?.name ?? '');
  const [isActive, setIsActive] = useState(team?.isActive ?? true);
  const save = useMutation({
    mutationFn: () =>
      team
        ? api.patch(`/api/teams/${team.id}`, { code, name, isActive })
        : api.post('/api/teams', { code, name, isActive }),
    onSuccess: () => onSaved(code.toUpperCase()),
  });

  function submit(event: FormEvent) {
    event.preventDefault();
    save.mutate();
  }

  return (
    <Dialog
      open
      onOpenChange={(open) => !open && onClose()}
      title={team ? `Editar equipo ${team.code}` : 'Nuevo equipo'}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          <Button form="team-form" type="submit" loading={save.isPending}>
            Guardar
          </Button>
        </>
      }
    >
      <form id="team-form" onSubmit={submit} className="grid gap-4">
        <Field label="Código" required>
          <Input className="uppercase" maxLength={20} value={code} onChange={(e) => setCode(e.target.value)} />
        </Field>
        <Field label="Nombre" required>
          <Input maxLength={120} value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Switch label="Activo" checked={isActive} onCheckedChange={setIsActive} />
        {save.isError && (
          <Alert tone="danger" title="No se ha podido guardar">
            {save.error instanceof ApiError ? save.error.message : 'Inténtalo de nuevo.'}
          </Alert>
        )}
      </form>
    </Dialog>
  );
}
